// MA-155 — math-diagnostic content-selection slice.
//
// ADR-058 shipped adaptiveStaircase.ts (the reversal-stop reducer) with ZERO
// live caller. ADR-081/ADR-082 wired it into a live READING phase appended
// to /onboarding/diagnostic. This module is the MATH-side counterpart of
// ADR-081: it still ships NO screen caller this round, but it closes the
// "math" half of MA-155's own acceptance sentence — "full variable-length
// (8-12 item) reversal-stop adaptive placement staircase, math + reading" —
// which the live math phase does NOT satisfy today. `diagnosticPlacement.ts`
// (MA-64) is a fixed 4-rung, 5-item bank; its own top-of-file comment says so
// explicitly: "this live screen stays fixed at 5 items ... expanding it is a
// separate, larger initiative ... tracked as its own backlog follow-up"
// (that follow-up is MA-155). See docs/adr/ADR-083-ma155-math-diagnostic-
// staircase-content.md for the full design + red-team record.
//
// Deliberately synthetic, not content-pack-derived — UNLIKE readingDiagnostic
// Content.ts. This is not a "sixth home" for pedagogical content: MATH_MODULES'
// arithmetic tiers (difficulty 4-8, addition/subtraction "operations" strand)
// are almost entirely `handwrite_then_confirm`-shaped in the real curriculum
// (see mathModules.js), so there is no wide tap-choice content pool to pull
// from the way reading had. `diagnosticPlacement.ts` already established the
// precedent of a hand-verified, runtime-generated addend-pair bank for
// exactly this live screen; this module extends that SAME precedent to a
// wider, real-module-difficulty-grounded ladder, rather than inventing a new
// content home.
//
// Ladder: 4 rungs, each citing a REAL MATH_MODULES id + its real `difficulty`
// field, strictly increasing (4, 5, 6, 8 — mirrors readingDiagnosticContent's
// own non-contiguous-but-increasing span). Difficulty 7 (math.12 "Missing
// addends within 20", math.13 "place value") is skipped: neither is an
// "a + b = ?" sum-shaped fact family — missing-addend and place-value are
// different item *types*, not a harder addition band — the same class of
// skip readingDiagnosticContent.ts documents for its own difficulty 2/4 gaps.

import { MATH_MODULES } from "../curriculum/mathModules.js";
import {
  initStaircase,
  STAIRCASE_MIN_ITEMS,
  STAIRCASE_MAX_ITEMS,
  STAIRCASE_REVERSAL_STOP_COUNT,
} from "./adaptiveStaircase";

interface MathStaircaseRung {
  moduleId: string;
  /** MATH_MODULES difficulty (0-8) this rung is grounded in. */
  moduleDifficulty: number;
  /** Hand-verified (a, b) addend pairs; every sum stays within the cited
   *  real module's own config bound (maxSum, or minA/maxA + minB/maxB for
   *  the two-digit rung) — see the bite test's t3/t4 for the proof. */
  pairs: [number, number][];
}

export const MATH_DIAGNOSTIC_STAIRCASE_LADDER: MathStaircaseRung[] = [
  // math.07 "Addition within 5" — maxSum: 5.
  { moduleId: "math.07", moduleDifficulty: 4, pairs: [[1, 1], [1, 2], [2, 2], [1, 3], [2, 3]] },
  // math.09 "Addition within 10" — maxSum: 10.
  { moduleId: "math.09", moduleDifficulty: 5, pairs: [[1, 5], [2, 5], [3, 5], [4, 5], [5, 5]] },
  // math.11 "Add/subtract within 20" — maxSum: 20 (addition subset only).
  { moduleId: "math.11", moduleDifficulty: 6, pairs: [[6, 5], [8, 5], [9, 7], [10, 8], [10, 10]] },
  // math.14 "Two-digit addition (no regrouping)" — minA:10/maxA:60,
  // minB:10/maxB:30; every pair's ones digits sum < 10 (no regrouping),
  // matching the cited module's own `regrouping: false` config.
  { moduleId: "math.14", moduleDifficulty: 8, pairs: [[21, 13], [32, 15], [40, 21], [53, 26], [14, 22]] },
];

// v1 calibration: reuse adaptiveStaircase's own shared constants rather than
// invent untested new ones (same choice readingDiagnosticContent.ts made) —
// conservative (over-, never under-) exposure bound for this narrower
// 4-rung ladder.
export const MATH_DIAGNOSTIC_STAIRCASE_CONFIG = {
  ladderSize: MATH_DIAGNOSTIC_STAIRCASE_LADDER.length,
  seedRung: 0,
  minItems: STAIRCASE_MIN_ITEMS,
  maxItems: STAIRCASE_MAX_ITEMS,
  reversalStopCount: STAIRCASE_REVERSAL_STOP_COUNT,
};

// A future live caller steps the staircase directly:
//   let state = initStaircase(MATH_DIAGNOSTIC_STAIRCASE_CONFIG);
//   state = stepStaircase(MATH_DIAGNOSTIC_STAIRCASE_CONFIG, state, wasCorrect);
// (no wrapper here, for the same reason readingDiagnosticContent.ts has
// none — delegating straight to the shared primitive keeps this module
// honest about doing zero staircase logic of its own.)

export interface MathDiagnosticStaircaseItem {
  id: string;
  prompt: string;
  correctAnswer: number;
}

/** MA-301 — the real MATH_MODULES id a finished staircase's terminal rung
 *  maps to (same clamp as mathDiagnosticStaircaseItemAt, since finalRung is
 *  the same rung-index space). Used to derive an evidence-backed placement
 *  floor, never to seed mastery — see placementFloor.ts's own doctrine. */
export function mathModuleIdForRung(rungIndex: number): string {
  const clamped = Math.max(0, Math.min(MATH_DIAGNOSTIC_STAIRCASE_LADDER.length - 1, rungIndex));
  return MATH_DIAGNOSTIC_STAIRCASE_LADDER[clamped].moduleId;
}

/** The real addend pair to serve at a given rung + rotating pick index
 *  (mirrors diagnosticItemAt's / readingDiagnosticItemAt's rotation so
 *  repeated rung visits don't always serve the identical pair). */
export function mathDiagnosticStaircaseItemAt(rungIndex: number, pick: number): MathDiagnosticStaircaseItem {
  const clamped = Math.max(0, Math.min(MATH_DIAGNOSTIC_STAIRCASE_LADDER.length - 1, rungIndex));
  const rung = MATH_DIAGNOSTIC_STAIRCASE_LADDER[clamped];
  const idx = ((pick % rung.pairs.length) + rung.pairs.length) % rung.pairs.length;
  const [a, b] = rung.pairs[idx];
  const correctAnswer = a + b;
  return {
    id: `math-staircase-${rung.moduleId}-${a}-${b}`,
    prompt: `${a} + ${b} = ?`,
    correctAnswer,
  };
}

/** Three distinct multiple-choice tiles (the correct sum + 2 distractors),
 *  deterministically shuffled from `seed`. Identical scheme to
 *  diagnosticPlacement.ts's proven-safe diagnosticChoices (distract1 =
 *  answer+1, distract2 = max(0, answer-2)) — both distractors stay distinct
 *  from the answer and each other for every sum this ladder can produce
 *  (verified by the bite test across the full ladder, incl. the two-digit
 *  rung). Not re-imported (diagnosticPlacement.ts stays untouched/frozen per
 *  MA-64's zero-regression contract) — the two-line scheme is duplicated
 *  intentionally rather than coupling this module to the frozen one. Takes
 *  the item (not a bare number) to match readingDiagnosticChoices' calling
 *  convention, so a future live caller can treat both domains uniformly. */
export function mathDiagnosticStaircaseChoices(item: MathDiagnosticStaircaseItem, seed: number): number[] {
  const answer = item.correctAnswer;
  const distract1 = answer + 1;
  const distract2 = Math.max(0, answer - 2);
  let s = seed;
  return [answer, distract1, distract2].sort(() => ((s += 1) % 2 ? -1 : 1));
}

export interface MathDiagnosticStaircaseItemResult {
  moduleId: string;
  itemId: string;
  chosen: number;
  correct: boolean;
}

export interface MathDiagnosticStaircaseResult {
  skill: "math_placement";
  correct: number;
  total: number;
  /** correct / total, in [0, 1]; 0 when there are no items. */
  accuracy: number;
  finalRung: number;
  /** MA-1120 — the rung the run PLACED at (diagnosticPlacementRung: the
   *  reducer's terminal rung, except that a final right answer stays on its
   *  rung), when the caller knows it. Written by both callers since MA-1120,
   *  and then `finalRung` carries the same value; readers prefer it and fall
   *  back to `finalRung` for rows written before (diagnosticResultRung). */
  placementRung?: number;
  reversalCount: number;
  items: MathDiagnosticStaircaseItemResult[];
  /** Deliberately distinct from diagnosticPlacement.ts's
   *  "onboarding_diagnostic" tag — nothing downstream could mistake this for
   *  a wired, screen-produced result (there is no screen yet). */
  source: "math_diagnostic_staircase";
  completedAt: string;
}

export function buildMathDiagnosticStaircaseResult(
  items: MathDiagnosticStaircaseItemResult[],
  finalState: ReturnType<typeof initStaircase>,
  placementRung?: number,
): MathDiagnosticStaircaseResult {
  const total = items.length;
  const correct = items.filter((i) => i.correct).length;
  return {
    skill: "math_placement",
    correct,
    total,
    accuracy: total ? correct / total : 0,
    finalRung: typeof placementRung === "number" ? placementRung : finalState.rung,
    ...(typeof placementRung === "number" ? { placementRung } : {}),
    reversalCount: finalState.reversalCount,
    items,
    source: "math_diagnostic_staircase",
    completedAt: new Date().toISOString(),
  };
}

// Re-exported for the bite test's real-module cross-check (proving every
// rung's declared moduleDifficulty and sum bound are genuinely grounded in
// the real MATH_MODULES entry, not fabricated).
export { MATH_MODULES as __mathModulesForTest };
